import { useEditor, EditorContent } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import Link from "@tiptap/extension-link"
import { TextSelection } from "@tiptap/pm/state"
import Superscript from "@tiptap/extension-superscript"
import Subscript from "@tiptap/extension-subscript"
import Underline from "@tiptap/extension-underline"
import { Table } from "@tiptap/extension-table"
import { TableRow } from "@tiptap/extension-table-row"
import { CustomTableCell } from "./TableCellExtension"
import { CustomTableHeader } from "./TableHeaderExtension"
import { CrSlotExtension } from "./CrSlotExtension"
import { CitationExtension } from "./CitationExtension"
import { Details, DetailsSummary } from "./DetailsExtension"
import { useState, useEffect, useCallback, useRef } from "react"
import { useStickyTableHeaders } from "@/lib/useStickyTableHeaders"
import { useCrTemplateKeys } from "@/lib/queries"
import { Button } from "@/components/ui/button"
import { Toggle } from "@/components/ui/toggle"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  List,
  ListOrdered,
  TableIcon,
  Link2,
  FileText,
  Quote,
  Trash2,
  ChevronsUpDown,
  SuperscriptIcon,
  SubscriptIcon,
  Undo2,
  Redo2,
  Pencil,
  Copy,
} from "lucide-react"
import { ScrollArea } from "@/components/ui/scroll-area"

interface TiptapEditorProps {
  content: string
  onChange: (html: string) => void
  isWorkflow?: boolean
  showCitationButton?: boolean
  toolbarStickyTop?: number
}

function setRowType(editor: ReturnType<typeof useEditor>, rowType: string | null) {
  if (!editor) return
  const { state, dispatch } = editor.view
  const { selection } = state
  const { $from } = selection

  // Walk up from the current position to find the table row
  let rowDepth = $from.depth
  while (rowDepth > 0 && $from.node(rowDepth).type.name !== "tableRow") {
    rowDepth--
  }
  if (rowDepth === 0) return

  const row = $from.node(rowDepth)
  const rowStart = $from.before(rowDepth)
  const tr = state.tr

  // Iterate cells in this row and set the rowType attribute
  let offset = 1 // skip the row node opening
  for (let i = 0; i < row.childCount; i++) {
    const cell = row.child(i)
    const cellPos = rowStart + offset
    tr.setNodeMarkup(cellPos, undefined, { ...cell.attrs, rowType })
    offset += cell.nodeSize
  }

  dispatch(tr)
  editor.commands.focus()
}


export function TiptapEditor({
  content,
  onChange,
  isWorkflow = false,
  showCitationButton = true,
  toolbarStickyTop = 0,
}: TiptapEditorProps) {
  const { data: crTemplateKeys } = useCrTemplateKeys()
  const crTemplateSections = crTemplateKeys ?? []

  const [citationPopoverOpen, setCitationPopoverOpen] = useState(false)
  const [citationCodes, setCitationCodes] = useState("")
  const [crSlotPopoverOpen, setCrSlotPopoverOpen] = useState(false)
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState("")
  const [linkName, setLinkName] = useState("")
  const [linkIsEditing, setLinkIsEditing] = useState(false)
  const [tablePopoverOpen, setTablePopoverOpen] = useState(false)
  const [tableRows, setTableRows] = useState(3)
  const [tableCols, setTableCols] = useState(3)
  const [contextMenu, setContextMenu] = useState<{
    x: number
    y: number
  } | null>(null)
  const [linkBubble, setLinkBubble] = useState<{
    x: number
    y: number
    href: string
  } | null>(null)
  const [citationBubble, setCitationBubble] = useState<{
    x: number
    y: number
    codes: string
    nodePos: number
  } | null>(null)
  const [citationEditPos, setCitationEditPos] = useState<number | null>(null)
  const [citationEditDialogOpen, setCitationEditDialogOpen] = useState(false)
  const editorWrapperRef = useRef<HTMLDivElement>(null)
  useStickyTableHeaders(editorWrapperRef, [content])

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false,
      }),
      // v3 dropped the `inclusive` option (it now follows `autolink`), so
      // set it on the mark itself: typing at a link's edge stays plain text.
      Link.extend({ inclusive: false }).configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-primary underline cursor-pointer",
          contenteditable: "false",
        },
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      CustomTableCell,
      CustomTableHeader,
      Superscript,
      Subscript,
      Underline,
      CrSlotExtension,
      CitationExtension,
      Details,
      DetailsSummary,
    ],
    content,
    onUpdate: ({ editor }) => {
      let html = editor.getHTML()
      // Strip trailing empty paragraphs that ProseMirror always adds
      html = html.replace(/(<p><\/p>|<p><br\s*\/?><\/p>)+$/i, "")
      onChange(html)
    },
    editorProps: {
      attributes: {
        class:
          "prose max-w-none focus:outline-none min-h-[200px] p-4",
      },
      handleDOMEvents: {
        click(view, event) {
          const target = event.target as HTMLElement
          const anchor = target.closest("a")
          if (anchor) {
            event.preventDefault()
          }
          return false
        },
      },
      handleClick(view, pos, event) {
        // Check for citation node click
        const $pos = view.state.doc.resolve(pos)
        const nodeBefore = $pos.nodeBefore
        const nodeAfter = $pos.nodeAfter
        const citationNode = (nodeBefore?.type.name === "citation" ? nodeBefore : null)
          ?? (nodeAfter?.type.name === "citation" ? nodeAfter : null)
        if (citationNode) {
          const nodePos = nodeBefore?.type.name === "citation"
            ? pos - nodeBefore.nodeSize
            : pos
          const coords = view.coordsAtPos(nodePos)
          setCitationBubble({
            x: coords.left,
            y: coords.bottom + 4,
            codes: citationNode.attrs.codes as string,
            nodePos,
          })
          setLinkBubble(null)
          return true
        }
        setCitationBubble(null)

        const clickedOnLink = (event.target as HTMLElement).closest('a')
        const linkMark = $pos.marks().find((m) => m.type.name === "link")
        if (linkMark && clickedOnLink) {
          // Find the full extent of this link mark
          let from = pos
          let to = pos
          const parent = $pos.parent
          const parentStart = $pos.start()
          parent.forEach((node, offset) => {
            const nodeFrom = parentStart + offset
            const nodeTo = nodeFrom + node.nodeSize
            if (node.marks.some((m) => m.type.name === "link" && m.attrs.href === linkMark.attrs.href)) {
              if (nodeFrom <= pos && pos < nodeTo) {
                from = nodeFrom
                to = nodeTo
              }
            }
          })

          // Select the full link text
          const { tr } = view.state
          tr.setSelection(TextSelection.create(view.state.doc, from, to))
          view.dispatch(tr)

          const coords = view.coordsAtPos(from)
          setLinkBubble({
            x: coords.left,
            y: coords.bottom + 4,
            href: linkMark.attrs.href,
          })
          return true
        }
        setLinkBubble(null)
        return false
      },
    },
  })

  // Sync editor content when the content prop changes externally
  // (e.g., when a modal resets its state to empty)
  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      const currentContent = editor.getHTML().replace(/(<p><\/p>|<p><br\s*\/?><\/p>)+$/i, "")
      if (content !== currentContent) {
        editor.commands.setContent(content || "")
      }
    }
  }, [content, editor])

  const closeContextMenu = useCallback(() => setContextMenu(null), [])
  const closeLinkBubble = useCallback(() => setLinkBubble(null), [])
  const closeCitationBubble = useCallback(() => setCitationBubble(null), [])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setContextMenu(null)
        setLinkBubble(null)
        setCitationBubble(null)
      }
    }
    window.addEventListener("scroll", closeContextMenu, true)
    window.addEventListener("scroll", closeLinkBubble, true)
    window.addEventListener("scroll", closeCitationBubble, true)
    window.addEventListener("keydown", handleKeyDown)
    return () => {
      window.removeEventListener("scroll", closeContextMenu, true)
      window.removeEventListener("scroll", closeLinkBubble, true)
      window.removeEventListener("scroll", closeCitationBubble, true)
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [closeContextMenu, closeCitationBubble])

  if (!editor) {
    return null
  }

  const insertCitation = () => {
    if (!citationCodes.trim()) return

    const codes = citationCodes
      .split(",")
      .map((c) => c.trim())
      .filter(Boolean)
      .join(", ")

    if (citationEditPos !== null) {
      // Replace the existing citation node at the stored position
      const nodeAtPos = editor.state.doc.nodeAt(citationEditPos)
      if (nodeAtPos && nodeAtPos.type.name === "citation") {
        editor
          .chain()
          .focus()
          .deleteRange({ from: citationEditPos, to: citationEditPos + nodeAtPos.nodeSize })
          .insertContentAt(citationEditPos, { type: "citation", attrs: { codes } })
          .run()
      }
      setCitationEditPos(null)
    } else {
      editor
        .chain()
        .focus()
        .insertContent({
          type: "citation",
          attrs: { codes },
        })
        .insertContent(" ")
        .run()
    }

    setCitationCodes("")
    setCitationPopoverOpen(false)
  }

  const insertCRSlot = (templateKey: string) => {
    editor
      .chain()
      .focus()
      .insertContent({
        type: "crSlot",
        attrs: { templateKey },
      })
      .insertContent(" ")
      .run()

    setCrSlotPopoverOpen(false)
  }

  const openLinkDialog = () => {
    // Pre-fill from existing link if cursor is on one
    const existingLink = editor.getAttributes("link")
    const { from, to } = editor.state.selection
    const selectedText = editor.state.doc.textBetween(from, to, "")
    if (existingLink.href) {
      setLinkUrl(existingLink.href)
      setLinkName(selectedText || "")
      setLinkIsEditing(true)
    } else {
      setLinkUrl("")
      setLinkName(selectedText)
      setLinkIsEditing(false)
    }
    setLinkDialogOpen(true)
  }

  const insertLink = () => {
    if (!linkUrl.trim()) return

    // Ensure URL has a protocol so it doesn't become a relative path
    let url = linkUrl.trim()
    if (!/^(https?:\/\/|mailto:|tel:)/i.test(url)) {
      url = "https://" + url
    }

    const displayText = linkName.trim() || url

    if (linkIsEditing || !editor.state.selection.empty) {
      // Replace selected link text with updated name and href
      editor
        .chain()
        .focus()
        .deleteSelection()
        .insertContent(`<a href="${url}" class="text-primary underline">${displayText}</a>`)
        .run()
    } else {
      // Insert new link at cursor
      editor
        .chain()
        .focus()
        .insertContent(
          `<a href="${url}" class="text-primary underline">${displayText}</a> `
        )
        .run()
    }

    setLinkUrl("")
    setLinkName("")
    setLinkIsEditing(false)
    setLinkDialogOpen(false)
  }

  const removeLink = () => {
    editor.chain().focus().unsetLink().run()
    setLinkUrl("")
    setLinkName("")
    setLinkIsEditing(false)
    setLinkDialogOpen(false)
  }

  const insertTable = () => {
    editor
      .chain()
      .focus()
      .insertTable({ rows: tableRows, cols: tableCols, withHeaderRow: true })
      .run()
    setTablePopoverOpen(false)
    setTableRows(3)
    setTableCols(3)
  }

  return (
    <div className="border rounded-lg">
      <div className="sticky z-10 bg-muted border-b p-2 flex flex-wrap gap-1 items-center rounded-t-lg" style={{ top: toolbarStickyTop }}>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo"
        >
          <Undo2 className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo"
        >
          <Redo2 className="h-4 w-4" />
        </Button>
        <Separator orientation="vertical" className="mx-1 h-6" />
        <Toggle
          size="sm"
          pressed={editor.isActive("bold")}
          onPressedChange={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("italic")}
          onPressedChange={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("underline")}
          onPressedChange={() => editor.chain().focus().toggleUnderline().run()}
          title="Underline"
        >
          <UnderlineIcon className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("superscript")}
          onPressedChange={() =>
            editor.chain().focus().toggleSuperscript().run()
          }
          title="Superscript"
        >
          <SuperscriptIcon className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("subscript")}
          onPressedChange={() =>
            editor.chain().focus().toggleSubscript().run()
          }
          title="Subscript"
        >
          <SubscriptIcon className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("heading", { level: 1 })}
          onPressedChange={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
          title="Heading 1"
        >
          <Heading1 className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("heading", { level: 2 })}
          onPressedChange={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
          title="Heading 2"
        >
          <Heading2 className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("heading", { level: 3 })}
          onPressedChange={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
          title="Heading 3"
        >
          <Heading3 className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("heading", { level: 4 })}
          onPressedChange={() =>
            editor.chain().focus().toggleHeading({ level: 4 }).run()
          }
          title="Heading 4"
        >
          <Heading4 className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("bulletList")}
          onPressedChange={() =>
            editor.chain().focus().toggleBulletList().run()
          }
        >
          <List className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("orderedList")}
          onPressedChange={() =>
            editor.chain().focus().toggleOrderedList().run()
          }
        >
          <ListOrdered className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("blockquote")}
          onPressedChange={() =>
            editor.chain().focus().toggleBlockquote().run()
          }
        >
          <Quote className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editor.isActive("details")}
          onPressedChange={() =>
            editor.chain().focus().insertCollapsible().run()
          }
          title="Collapsible block"
        >
          <ChevronsUpDown className="h-4 w-4 mr-1" />
          <span className="text-xs">Details</span>
        </Toggle>
        <Popover open={tablePopoverOpen} onOpenChange={setTablePopoverOpen}>
          <PopoverTrigger asChild>
            <Button size="sm" variant="ghost">
              <TableIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Insert Table</Label>
                <div className="flex items-center gap-3">
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="table-rows" className="text-xs text-muted-foreground">Rows</Label>
                    <Input
                      id="table-rows"
                      type="number"
                      min={1}
                      max={20}
                      value={tableRows}
                      onChange={(e) => setTableRows(parseInt(e.target.value) || 1)}
                    />
                  </div>
                  <div className="flex-1 space-y-1">
                    <Label htmlFor="table-cols" className="text-xs text-muted-foreground">Columns</Label>
                    <Input
                      id="table-cols"
                      type="number"
                      min={1}
                      max={10}
                      value={tableCols}
                      onChange={(e) => setTableCols(parseInt(e.target.value) || 1)}
                    />
                  </div>
                </div>
              </div>
              <Button size="sm" className="w-full" onClick={insertTable}>
                Insert Table
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        {editor.isActive("details") && (
          <div className="flex items-center gap-0.5 border-l pl-1 ml-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                editor.chain().focus().removeCollapsible().run()
              }
              title="Remove collapsible wrapper"
            >
              <Trash2 className="h-4 w-4 mr-0.5" />
              <span className="text-xs">Unwrap</span>
            </Button>
          </div>
        )}

        <Button
          size="sm"
          variant={editor.isActive("link") ? "secondary" : "ghost"}
          onClick={openLinkDialog}
        >
          <Link2 className="h-4 w-4" />
        </Button>
        <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{linkIsEditing ? "Edit Link" : "Insert Link"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="link-name">Link Name</Label>
                <Input
                  id="link-name"
                  value={linkName}
                  onChange={(e) => setLinkName(e.target.value)}
                  placeholder="Display text"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="link-url">URL</Label>
                <Input
                  id="link-url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://example.com"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      insertLink()
                    }
                  }}
                />
              </div>
            </div>
            <DialogFooter className="flex justify-between sm:justify-between">
              {linkIsEditing && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={removeLink}
                >
                  Remove Link
                </Button>
              )}
              <div className="flex gap-2 ml-auto">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setLinkUrl("")
                    setLinkName("")
                    setLinkIsEditing(false)
                    setLinkDialogOpen(false)
                  }}
                >
                  Cancel
                </Button>
                <Button size="sm" onClick={insertLink} disabled={!linkUrl.trim()}>
                  {linkIsEditing ? "Update" : "Insert"}
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {showCitationButton && (
          <Popover
            open={citationPopoverOpen}
            onOpenChange={setCitationPopoverOpen}
          >
            <PopoverTrigger asChild>
              <Button size="sm" variant="ghost">
                <FileText className="h-4 w-4 mr-1" />
                Citation
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="citation-codes">Citation Codes</Label>
                  <Input
                    id="citation-codes"
                    value={citationCodes}
                    onChange={(e) => setCitationCodes(e.target.value)}
                    placeholder="CODE1, CODE2, CODE3"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault()
                        insertCitation()
                      }
                    }}
                  />
                  <p className="text-xs text-muted-foreground">
                    Separate multiple codes with commas
                  </p>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setCitationCodes("")
                      setCitationPopoverOpen(false)
                    }}
                  >
                    Cancel
                  </Button>
                  <Button size="sm" onClick={insertCitation}>
                    Insert
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {isWorkflow && (
          <Popover
            open={crSlotPopoverOpen}
            onOpenChange={setCrSlotPopoverOpen}
          >
            <PopoverTrigger asChild>
              <Button size="sm" variant="ghost">
                CR Section
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-96">
              <div className="space-y-2">
                <Label>Select Clinical Review Section</Label>
                <ScrollArea className="h-72">
                  <div className="space-y-1">
                    {crTemplateSections.map((section) => (
                      <Button
                        key={section.template_key}
                        variant="ghost"
                        className="w-full justify-start text-left font-normal"
                        onClick={() =>
                          insertCRSlot(section.template_key)
                        }
                      >
                        {section.heading}
                      </Button>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
      <div
        ref={editorWrapperRef}
        onContextMenu={(e) => {
          if (editor.isActive("table")) {
            e.preventDefault()
            setContextMenu({ x: e.clientX, y: e.clientY })
          }
        }}
      >
        <EditorContent editor={editor} />
      </div>

      {linkBubble && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={closeLinkBubble}
          />
          <div
            className="fixed z-50 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
            style={{ left: linkBubble.x, top: linkBubble.y }}
          >
            <div className="flex items-center">
              <button
                className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors"
                onClick={() => {
                  let href = linkBubble.href
                  if (!/^(https?:\/\/|mailto:|tel:)/i.test(href)) {
                    href = "https://" + href
                  }
                  window.open(href, "_blank", "noopener,noreferrer")
                  closeLinkBubble()
                }}
              >
                <Link2 className="h-3.5 w-3.5" />
                Open Link
              </button>
              <div className="w-px h-6 bg-border" />
              <button
                className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors"
                onClick={() => {
                  closeLinkBubble()
                  openLinkDialog()
                }}
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit Link
              </button>
              <div className="w-px h-6 bg-border" />
              <button
                className="flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors"
                onClick={() => {
                  closeLinkBubble()
                  removeLink()
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Remove
              </button>
            </div>
          </div>
        </>
      )}

      {citationBubble && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={closeCitationBubble}
          />
          <div
            className="fixed z-50 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md"
            style={{ left: citationBubble.x, top: citationBubble.y }}
          >
            <div className="flex items-center">
              <button
                className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors"
                onClick={() => {
                  const codes = citationBubble.codes
                  const html = `<span data-citation="" data-codes="${codes}" class="compliance-citation">[${codes}]</span>`
                  const blob = new Blob([html], { type: "text/html" })
                  const textBlob = new Blob([`[${codes}]`], { type: "text/plain" })
                  navigator.clipboard.write([
                    new ClipboardItem({
                      "text/html": blob,
                      "text/plain": textBlob,
                    }),
                  ])
                  closeCitationBubble()
                }}
              >
                <Copy className="h-3.5 w-3.5" />
                Copy
              </button>
              <div className="w-px h-6 bg-border" />
              <button
                className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent transition-colors"
                onClick={() => {
                  setCitationEditPos(citationBubble.nodePos)
                  setCitationCodes(citationBubble.codes)
                  closeCitationBubble()
                  setCitationEditDialogOpen(true)
                }}
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </button>
              <div className="w-px h-6 bg-border" />
              <button
                className="flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors"
                onClick={() => {
                  const nodeAtPos = editor.state.doc.nodeAt(citationBubble.nodePos)
                  if (nodeAtPos && nodeAtPos.type.name === "citation") {
                    editor
                      .chain()
                      .focus()
                      .deleteRange({
                        from: citationBubble.nodePos,
                        to: citationBubble.nodePos + nodeAtPos.nodeSize,
                      })
                      .run()
                  }
                  closeCitationBubble()
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </button>
            </div>
          </div>
        </>
      )}

      <Dialog open={citationEditDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setCitationEditDialogOpen(false)
          setCitationEditPos(null)
          setCitationCodes("")
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Citation</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="citation-edit-codes">Citation Codes</Label>
            <Input
              id="citation-edit-codes"
              value={citationCodes}
              onChange={(e) => setCitationCodes(e.target.value)}
              placeholder="CODE1, CODE2, CODE3"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  insertCitation()
                  setCitationEditDialogOpen(false)
                }
              }}
            />
            <p className="text-xs text-muted-foreground">
              Separate multiple codes with commas
            </p>
          </div>
          <DialogFooter>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setCitationEditDialogOpen(false)
                setCitationEditPos(null)
                setCitationCodes("")
              }}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={() => {
              insertCitation()
              setCitationEditDialogOpen(false)
            }}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {contextMenu && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={closeContextMenu}
          />
          <div
            className="fixed z-50 min-w-[12rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
            style={{ left: contextMenu.x, top: contextMenu.y }}
          >
            <ContextMenuItem onClick={() => { editor.chain().focus().addRowBefore().run(); closeContextMenu() }}>
              Add Row Above
            </ContextMenuItem>
            <ContextMenuItem onClick={() => { editor.chain().focus().addRowAfter().run(); closeContextMenu() }}>
              Add Row Below
            </ContextMenuItem>
            <ContextMenuItem onClick={() => { editor.chain().focus().addColumnBefore().run(); closeContextMenu() }}>
              Add Column Before
            </ContextMenuItem>
            <ContextMenuItem onClick={() => { editor.chain().focus().addColumnAfter().run(); closeContextMenu() }}>
              Add Column After
            </ContextMenuItem>
            <div className="-mx-1 my-1 h-px bg-muted" />
            <ContextMenuItem
              onClick={() => { editor.chain().focus().mergeCells().run(); closeContextMenu() }}
              disabled={!editor.can().mergeCells()}
            >
              Merge Cells
            </ContextMenuItem>
            <ContextMenuItem
              onClick={() => { editor.chain().focus().splitCell().run(); closeContextMenu() }}
              disabled={!editor.can().splitCell()}
            >
              Split Cell
            </ContextMenuItem>
            <div className="-mx-1 my-1 h-px bg-muted" />
            <ContextMenuItem onClick={() => { setRowType(editor, "header"); closeContextMenu() }}>
              Make Row Header
            </ContextMenuItem>
            <ContextMenuItem onClick={() => { setRowType(editor, "subheader"); closeContextMenu() }}>
              Make Row Subheader
            </ContextMenuItem>
            <ContextMenuItem onClick={() => { setRowType(editor, null); closeContextMenu() }}>
              Remove Row Style
            </ContextMenuItem>
            <div className="-mx-1 my-1 h-px bg-muted" />
            <ContextMenuItem onClick={() => { editor.chain().focus().deleteRow().run(); closeContextMenu() }}>
              Delete Row
            </ContextMenuItem>
            <ContextMenuItem onClick={() => { editor.chain().focus().deleteColumn().run(); closeContextMenu() }}>
              Delete Column
            </ContextMenuItem>
            <ContextMenuItem
              onClick={() => { editor.chain().focus().deleteTable().run(); closeContextMenu() }}
              destructive
            >
              Delete Table
            </ContextMenuItem>
          </div>
        </>
      )}
    </div>
  )
}

function ContextMenuItem({
  onClick,
  disabled,
  destructive,
  children,
}: {
  onClick: () => void
  disabled?: boolean
  destructive?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      className={`relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50 ${destructive ? "text-destructive hover:bg-destructive/10" : ""}`}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  )
}
